import React, { useState, useCallback, useRef } from 'react'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { Button, Flex, Text } from 'vvs-uikit'
import Tokens, { tokensByAddress } from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import { useAppDispatch } from 'state'
import { fetchFarmUserDataAsync } from 'state/farms'
import { DeserializedFarm } from 'state/types'
import { FarmRewarders } from 'config/constants/types'
import useToast from 'hooks/useToast'
import { getBalanceAmount, formatTokenAmount } from 'utils/formatBalance'
import { getAddress } from 'utils/addressHelpers'
import { BIG_ZERO } from 'utils/bigNumber'
import { useWeb3React } from '@web3-react/core'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { useTransactionAdder } from 'state/transactions/hooks'
import LocalBalance from 'components/LocalBalance'
import QuestionHelper from 'components/QuestionHelper'
import { TOKEN_PRICE_MAPPING } from 'config/constants/tokenPriceMappings'
import { useHarvestBoostByPid } from 'hooks/boost-farm/useFarmBoostContract'
import { renderBoostEarnings } from 'views/Farms/utils'
import isBoostFarm from 'utils/isBoostFarm'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useHarvestFarm from '../../hooks/useHarvestFarm'
import useHarvestFarmV2 from '../../hooks/useHarvestFarmV2'

const BoostedBox = styled(Flex)`
  border-top: 1px solid ${({ theme }) => theme.colors.boostDivider};
  padding-top: 12px;
`

interface FarmCardBoostActionsProps {
  boostReward?: BigNumber
  boostRewardAllFarms?: BigNumber
  pid?: number
  lpLabel: string
}

interface FarmCardActionsProps extends FarmCardBoostActionsProps {
  earnings?: BigNumber
  version?: number
  earningsV2?: any
  farms?: DeserializedFarm[]
  isBoosted?: boolean
  poolWeight?: BigNumber
  rewardData?: any[]
  rewarders?: FarmRewarders[]
}

const HarvestBoostAction: React.FC<FarmCardBoostActionsProps> = ({
  pid,
  lpLabel,
  boostReward,
  boostRewardAllFarms,
}) => {
  const dispatch = useAppDispatch()
  const { account } = useActiveWeb3React()
  const { toastError } = useToast()
  const { t } = useTranslation()
  const vvsPrice = usePriceVvsUsdc()
  const addTransaction = useTransactionAdder()
  const { mutateAsync: harvestBoost, isLoading: isHarvesting } = useHarvestBoostByPid()
  const hasReward = boostRewardAllFarms?.gt(0) || boostReward?.gt(0)
  const anyLoading = isHarvesting

  const harvestBoostSummary = useRef('')
  harvestBoostSummary.current = t('Harvest Boost %token% from %lpLabel% farm', {
    token: Tokens.vvs.symbol,
    lpLabel,
  })
  const clickHarvestBoost = useCallback(async () => {
    try {
      const { tx } = await harvestBoost(pid)
      addTransaction(tx, {
        title: t('Your Rewards Have Been Deposited In Your Wallet'),
        summary: harvestBoostSummary.current,
      })
    } catch (e) {
      toastError(
        t('Error'),
        t(
          'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
        ),
      )
      console.error(e)
    }
    dispatch(fetchFarmUserDataAsync({ account, pids: [pid] }))
  }, [harvestBoost, pid, t, addTransaction, toastError, account, dispatch])

  return (
    <BoostedBox flexDirection="column" gap="10px">
      <Flex flexDirection="column">
        <Text textTransform="uppercase" fontSize="13px">
          {t('boosted rewards')}
        </Text>
        <Flex justifyContent="space-between" alignItems="start">
          {renderBoostEarnings(boostReward, vvsPrice)}
          <Button disabled={anyLoading || !hasReward} onClick={clickHarvestBoost} ml="4px">
            {t('Harvest')}
          </Button>
        </Flex>
      </Flex>
      {boostRewardAllFarms?.gt(0) && (
        <Flex flexDirection="column">
          <Flex>
            <Text textTransform="uppercase" fontSize="13px">
              {t('pending rewards')}
            </Text>
            <QuestionHelper
              text={t('Pending rewards are boosted rewards which you have not harvest previously')}
              placement="top-start"
              mt="2px"
              ml="4px"
            />
          </Flex>
          <Flex justifyContent="space-between" alignItems="start">
            {renderBoostEarnings(boostRewardAllFarms, vvsPrice)}
          </Flex>
        </Flex>
      )}
    </BoostedBox>
  )
}

const HarvestAction: React.FC<FarmCardActionsProps> = ({
  earnings,
  pid,
  version,
  earningsV2,
  farms,
  isBoosted,
  lpLabel,
  poolWeight,
  rewardData,
  rewarders,
  boostReward,
  boostRewardAllFarms,
}) => {
  const { account } = useWeb3React()
  const { toastError } = useToast()
  const { t } = useTranslation()
  const [pendingTx, setPendingTx] = useState(false)
  const { onReward } = useHarvestFarm(pid)
  const { onReward: onRewardV2 } = useHarvestFarmV2(pid)
  const addTransaction = useTransactionAdder()
  const vvsPrice = usePriceVvsUsdc()
  const dispatch = useAppDispatch()
  let EnableHarvestButton = false
  const vvsRewards = account ? getBalanceAmount(earnings) : BIG_ZERO
  const vvsRewardsUsdc = vvsRewards ? vvsRewards.times(vvsPrice) : BIG_ZERO
  if (vvsRewardsUsdc.gt(0)) EnableHarvestButton = true

  const clickHarvestButton = async () => {
    setPendingTx(true)
    try {
      if (version === 2) {
        const { tx } = await onRewardV2()
        addTransaction(tx, {
          title: t('Your Rewards Have Been Deposited In Your Wallet'),
          summary: t('Harvest %tokens% from %lpLabel% farm', {
            tokens: earningsV2.tokens.map((address: string) => tokenListByAddress[`${address}`]?.symbol).join(', '),
            lpLabel,
          }),
        })
      } else {
        const { tx } = await onReward()
        addTransaction(tx, {
          title: t('Your Rewards Have Been Deposited In Your Wallet'),
          summary: t('Harvest %token% from %lpLabel% farm', {
            token: Tokens.vvs.symbol,
            lpLabel,
          }),
        })
      }
    } catch (e) {
      toastError(
        t('Error'),
        t(
          'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
        ),
      )
      console.error(e)
    } finally {
      setPendingTx(false)
    }
    dispatch(fetchFarmUserDataAsync({ account, pids: [pid] }))
  }

  const tokenListByAddress = tokensByAddress()

  const renderEarningsV1 = (): JSX.Element => {
    const token = Tokens.vvs
    return (
      <Flex key={token.symbol} flexDirection="column" mt="6px">
        <Text fontSize="18px" bold>{`${token.symbol} ${formatTokenAmount(token, vvsRewards)}`}</Text>
        {vvsRewardsUsdc.gt(0) ? (
          <LocalBalance fontSize="12px" color="textSubtle" decimals={2} value={vvsRewardsUsdc} prefix="~" />
        ) : (
          <Text />
        )}
      </Flex>
    )
  }

  const renderEarningsV2 = (): JSX.Element => {
    return (
      <Flex flexDirection="column" mt="6px">
        {earningsV2.tokens.map((address: string, index: number) => {
          const token = tokenListByAddress[`${address}`]
          const pendingReward: BigNumber = getBalanceAmount(earningsV2.earnings[index], token.decimals)

          // token price
          let tokenPriceUsd: string = null
          let isActive: boolean
          if (address === Tokens.vvs.address) {
            tokenPriceUsd = vvsPrice.toFixed()
            isActive = poolWeight.gt(0)
          } else {
            const mappedTokenAddr = TOKEN_PRICE_MAPPING[address.toLowerCase()] ?? address.toLowerCase()
            const getFarm = farms.find((farm) => farm.token.address.toLowerCase() === mappedTokenAddr)
            tokenPriceUsd = getFarm ? getFarm.tokenPriceUsdc : 'NaN'
            // FIXME: Workaround with index to identify duplicated reward tokens
            const currentRewarder =
              index > 0 && rewarders && rewarders[index - 1]?.token.address.toLowerCase() === address.toLowerCase()
                ? rewarders[index - 1]
                : null
            const currentReward = rewardData?.find((r) => r.address === getAddress(currentRewarder?.address))
            const now = Date.now() / 1000
            isActive =
              !currentRewarder?.isFinished &&
              (!currentReward ||
                (new BigNumber(currentReward.allocPoint).gt(0) &&
                  parseInt(currentReward.rewardStartTimestamp) <= now &&
                  now <= parseInt(currentReward.rewardEndTimestamp)))
          }

          // token price * amount
          const earningValue = pendingReward.times(tokenPriceUsd)
          if (earningValue.gt(0)) {
            EnableHarvestButton = true
          } else if (!isActive) {
            return null
          }

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Flex key={`${address}-${index}`} flexDirection="column" mt="6px" mb="10px">
              <Text fontSize="18px" bold>{`${token.symbol} ${formatTokenAmount(token, pendingReward)}`}</Text>
              {earningValue.gt(0) ? (
                <LocalBalance fontSize="12px" color="textSubtle" decimals={2} value={earningValue} prefix="~" />
              ) : (
                <Text />
              )}
            </Flex>
          )
        })}
      </Flex>
    )
  }

  return (
    <Flex flexDirection="column" gap="12px" mb="4px">
      <Flex flexDirection="column">
        <Text textTransform="uppercase" fontSize="13px">
          {t('earned rewards')}
        </Text>
        <Flex justifyContent="space-between" alignItems="start">
          {version === 2 && earningsV2 ? renderEarningsV2() : renderEarningsV1()}
          <Button disabled={!EnableHarvestButton || pendingTx} onClick={clickHarvestButton}>
            {t('Harvest')}
          </Button>
        </Flex>
      </Flex>
      {(isBoostFarm(farms?.find((e) => e.pid === pid)) || isBoosted) && (
        <HarvestBoostAction
          pid={pid}
          lpLabel={lpLabel}
          boostReward={boostReward}
          boostRewardAllFarms={boostRewardAllFarms}
        />
      )}
    </Flex>
  )
}

export default HarvestAction
